import config from 'config';
import { connect } from 'react-redux';
import { compose, withHandlers, lifecycle, withState, withProps } from 'recompose';
import moment from 'moment';
import { injectIntl } from 'react-intl';
import Promise from 'promise-polyfill';

import { pushWithQuery as push } from 'utils/routing';
import * as vehicleActions from 'actions/vehicles';

import { getQuerySearch } from 'reselects';
import { getCurrentVehicle } from 'redux/modules/vehicle-section/selectors';
import { vehicleDetailMounted } from 'redux/modules/vehicle-section/effects';
import { withRouter } from 'react-router-dom';
import VehicleDetail from './component';

const actionCreators = {
  ...vehicleActions,
  vehicleDetailMounted,
};

const enhance = compose(
  injectIntl,
  withRouter,
  connect((state, ownProps) => {
    return {
      query: getQuerySearch(ownProps),
      vehicle: getCurrentVehicle(state),
      availableVehiclesTypes: state.me.country_settings.available_vehicle_types,
    };
  }, actionCreators),
  withState('stateVehicle', 'setVehicle', {}),
  withState('fieldErrors', 'setFieldError', []),
  withState('submitting', 'setSubmitting', false),
  withState('serverErrors', 'setServerError', {}),
  withProps(() => ({
    dateList: [
      'road_permit_expiration',
      'conformity_expiration',
      'transport_card_expiration',
      'itv_expiration',
    ],
    vehicleTypesToIconsName: {
      van: 'van',
      rigid_truck: 'rigid',
      semi_trailer: 'semi-trailer',
      tractor_unit: 'tractor',
      box_van: 'box-van',
      trailer: 'trailer',
    },
  })),
  withHandlers({
    handleCloseModal:
      ({ query }) =>
      () => {
        push({ pathname: `${config.BASE_ROUTE}vehicle/`, query });
      },
  }),
  withHandlers({
    handleUpdateVehicle:
      ({ updateVehicle, handleCloseModal }) =>
      (vehicle) => {
        return updateVehicle({ ...vehicle, carrier: undefined }).then(handleCloseModal);
      },
    handleServerError:
      ({ setServerError }) =>
      (errors) => {
        setServerError(errors);
      },
    handleShowFieldError:
      ({ fieldErrors }) =>
      (field) => {
        return fieldErrors.indexOf(field) !== -1;
      },
    handleFieldChange:
      ({ stateVehicle, setVehicle, fieldErrors, setFieldError, setServerError }) =>
      (
        field,
        clearErrors = [],
        cb = () => {
          /*noop*/
        }
      ) => {
        return (value) => {
          setVehicle(
            {
              ...stateVehicle,
              [field]: value === '' ? null : value,
            },
            () => cb()
          );
          setFieldError(fieldErrors.filter((e) => clearErrors.concat(field).indexOf(e) === -1));
          setServerError({});
        };
      },
    handleValidateVehicle:
      ({ stateVehicle, setFieldError }) =>
      () => {
        const validDate = (value) => !value || moment(value).isValid();

        const validations = {
          plate_number: (value) => value && value !== '',
          vehicle_type: (value) => value && value !== '',
          temperature_details: (value) => value && value.type,
          insurance_expiration: validDate,
          road_permit_expiration: validDate,
          conformity_expiration: validDate,
          transport_card_expiration: validDate,
          itv_expiration: validDate,
          has_thermograph: (value, vehicle) =>
            (vehicle.temperature_details && vehicle.temperature_details.type === 'DRY') ||
            (vehicle.temperature_details.type !== 'DRY' &&
              typeof value !== 'undefined' &&
              value !== null),
        };

        return new Promise((resolve, reject) => {
          const fieldErrors = Object.keys(validations)
            .map((field) => (validations[field](stateVehicle[field], stateVehicle) ? null : field))
            .filter((error) => error);

          if (fieldErrors.length === 0) {
            resolve();
          } else {
            setFieldError(fieldErrors);
            reject();
          }
        });
      },
    handleTemperatureChange:
      ({ stateVehicle, fieldErrors, setVehicle, setFieldError, setServerError }) =>
      (field, { clearErrors = [], resetFields = [], resetCommonFields = [] } = {}) => {
        return (value) => {
          const resetVehicleFields = {};
          const temperature_details = {
            ...stateVehicle.temperature_details,
            [field]: value === '' ? null : value,
          };

          resetFields.forEach((field) => (temperature_details[field] = null));
          resetCommonFields.forEach((field) => (resetVehicleFields[field] = null));

          setVehicle({
            ...stateVehicle,
            temperature_details,
            ...resetVehicleFields,
          });
          setFieldError(fieldErrors.filter((e) => clearErrors.indexOf(e) === -1));
          setServerError({});
        };
      },
  }),
  withHandlers({
    handleSave:
      ({
        onSaveSuccesfully,
        stateVehicle,
        handleValidateVehicle,
        setSubmitting,
        handleUpdateVehicle,
        handleServerError,
      }) =>
      () => {
        handleValidateVehicle().then(() => setSubmitting(true));
        handleUpdateVehicle(stateVehicle)
          .then(onSaveSuccesfully)
          .catch((err) => err.response.json().then(handleServerError));
        setSubmitting(false);
      },
  }),
  lifecycle({
    componentDidMount() {
      const { vehicleDetailMounted, match } = this.props;
      const { id } = match.params;
      vehicleDetailMounted(id);
    },
    // All the old logic from the detail is based on using a copy
    // of the vehicle obj. in the state:
    componentDidUpdate(prevProps) {
      const { vehicle, setVehicle } = this.props;

      if (vehicle !== prevProps.vehicle) {
        setVehicle({
          ...vehicle,
          temperature_details: (vehicle && vehicle.temperature_details) || {},
        });
      }
    },
  })
);

export default enhance(VehicleDetail);
